import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useFloorPlan, VisualizationOptions } from '../FloorPlanContext';
import { Search } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../../../config';
import './VisualizationPanel.css';
import { greasedLineVertexShader } from '@babylonjs/core';
import { getValidAccessToken } from '../../../Utility/authUtils';

interface VisualizationPanelProps {
  onClose: () => void;
}

const FLOOR_TEXTURES = [
  { id: 'grass_diff', name: 'Grass' },
  { id: 'asphalt_floor_diff', name: 'Asphalt' },
  { id: 'floor_tiles_diff', name: 'Floor Tiles' },
  { id: 'granite_tile_diff', name: 'Granite' },
  { id: 'marble_diff', name: 'Marble' },
  { id: 'plank_flooring_diff', name: 'Plank' },
  { id: 'rubber_tiles_diff', name: 'Rubber Tiles' },
  { id: 'square_floor_diff', name: 'Square Floor' },
  { id: 'tiled_floor_diffuse', name: 'Tiled Floor' },
  { id: 'wood_floor_diffuse', name: 'Wood Floor' },
  // { id: 'grass_diff2', name: 'Grass2' },
];

interface Point {
  x: number;
  z: number;
}

interface Room {
  id: string;
  room_type: string;
  area: number;
  floor_polygon: Point[];
  isBoundary?: boolean;
}

const VisualizationPanel: React.FC<VisualizationPanelProps> = ({ onClose }) => {
  const { visualizationOptions, updateVisualizationOption, resetVisualizationOptions, floorPlanData, setFloorPlanData } = useFloorPlan();
  const panelRef = useRef<HTMLDivElement>(null);
  const { projectId, planId } = useParams();
  const API_BASE_URL = API_URL;
  
  // Custom texture mode state
  const [selectedRooms, setSelectedRooms] = useState<Set<string>>(new Set());
  const [selectedTexture, setSelectedTexture] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('#f0f0f0');
  const [textureSearch, setTextureSearch] = useState('');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'texture' | 'color'>('texture');
  const [isSaving, setIsSaving] = useState(false);
  const textureGridRef = useRef<HTMLDivElement>(null);

  // Get rooms for mini floor plan
  const rooms = useMemo(() => {
    if (!floorPlanData || !floorPlanData.rooms) return [];
    return floorPlanData.rooms.filter((room: Room) => 
      room.room_type !== 'Reference' && room.room_type !== 'Wall' && room.room_type !== 'Boundary'
    );
  }, [floorPlanData]);

  // Load saved customization from floorPlanData on mount
  useEffect(() => {
    if (floorPlanData && (floorPlanData as any).customization) {
      const customization = (floorPlanData as any).customization;
      
      // Check if floor customization exists
      if (customization.floor) {
        const floorKeys = Object.keys(customization.floor);
        
        if (floorKeys.length === 0) {
          // Floor customizations were cleared - reset to empty
          const currentTextures = visualizationOptions.customRoomTextures || {};
          if (Object.keys(currentTextures).length > 0) {
            updateVisualizationOption('customRoomTextures', {});
          }
        } else {
          // Floor has customizations - load them
          const newCustomTextures: { [roomId: string]: string } = {};
          
          Object.entries(customization.floor).forEach(([roomId, floorData]: [string, any]) => {
            if (floorData.texture) {
              newCustomTextures[roomId] = floorData.texture;
            } else if (floorData.color) {
              newCustomTextures[roomId] = `color:${floorData.color}`;
            }
          });
          
          if (Object.keys(newCustomTextures).length > 0) {
            updateVisualizationOption('customRoomTextures', newCustomTextures);
            // Don't auto-switch colorScheme - let it load from localStorage
          }
        }
      }
    }
  }, [floorPlanData]); // Only run when floorPlanData changes

  const blockAllEvents = (e: React.MouseEvent | React.WheelEvent) => {
    e.stopPropagation();
  };

  const blockWheelEvents = (e: React.WheelEvent) => {
    e.stopPropagation();
    e.preventDefault(); 
  };
  
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const handleWheel = (e: WheelEvent) => {
      // Only stop propagation to prevent 3D scene from zooming
      // Don't prevent default - allow normal scrolling
      e.stopPropagation();
    };

    panel.addEventListener('wheel', handleWheel, { passive: true });
    
    return () => {
      panel.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleToggle = (option: keyof VisualizationOptions) => {
    if (typeof visualizationOptions[option] === 'boolean') {
      updateVisualizationOption(
        option,
        !visualizationOptions[option] as any
      );
    }
  };

  const handleSliderChange = (option: keyof VisualizationOptions, value: number) => {
    updateVisualizationOption(option, value as any);
  };

  const handleSelectChange = (option: keyof VisualizationOptions, value: string) => {
    updateVisualizationOption(option, value as any);
  };

  const handleColorSampleClick = (colorScheme: string) => {
    handleSelectChange('colorScheme', colorScheme as any);
  };

  // Custom texture handlers
  const handleRoomSelect = (roomId: string) => {
    if (multiSelectMode) {
      setSelectedRooms(prev => {
        const newSet = new Set(prev);
        if (newSet.has(roomId)) {
          newSet.delete(roomId);
        } else {
          newSet.add(roomId);
        }
        return newSet;
      });
    } else {
      setSelectedRooms(new Set([roomId]));
    }
  };

  const handleSelectAllRooms = () => {
    if (selectedRooms.size === rooms.length) {
      setSelectedRooms(new Set());
    } else {
      setSelectedRooms(new Set(rooms.map((r: Room) => r.id)));
    }
  };

  const handleApplyTexture = async () => {
    if (selectedRooms.size === 0 || (!selectedTexture && activeTab === 'texture') || (!selectedColor && activeTab === 'color')) return;
    
    const roomIds = Array.from(selectedRooms);
    
    // Save to backend and localStorage FIRST
    const success = await saveCustomizationToBackend(roomIds, activeTab === 'texture' ? selectedTexture : `color:${selectedColor}`);
    
    // Only update local state if save was successful
    if (success) {
      const newCustomTextures = { ...(visualizationOptions.customRoomTextures || {}) };
      
      if (activeTab === 'texture') {
        roomIds.forEach(roomId => {
          newCustomTextures[roomId] = selectedTexture;
        });
      } else {
        roomIds.forEach(roomId => {
          newCustomTextures[roomId] = `color:${selectedColor}`;
        });
      }
      
      updateVisualizationOption('customRoomTextures', newCustomTextures);
      setSelectedRooms(new Set());
    }
  };

  // Save customization to backend and localStorage
  const saveCustomizationToBackend = async (roomIds: string[], textureOrColor: string): Promise<boolean> => {
    if (!projectId || !planId) {
      console.error('Missing projectId or planId');
      return false;
    }

    try {
      setIsSaving(true);
      const token = await getValidAccessToken();
      if (!token) {
        console.error('No access token found');
        setIsSaving(false);
        return false;
      }

      // Get current customization or create new one (matching 3D structure)
      const currentCustomization = (floorPlanData as any)?.customization || { wall: {}, floor: {} };
      
      // Update customization (EXACT same structure as 3D panel)
      const updatedCustomization = { ...currentCustomization };
      
      if (!updatedCustomization.floor) {
        updatedCustomization.floor = {};
      }
      
      // Check if it's a color or texture
      const isColor = textureOrColor.startsWith('color:');
      const actualValue = isColor ? textureOrColor.substring(6) : textureOrColor;
      
      // Apply the same customization to all selected rooms
      roomIds.forEach(roomId => {
        updatedCustomization.floor[roomId] = {
          ...(isColor ? { color: actualValue } : { texture: actualValue })
        };
      });

      // Merge with floor plan data
      const updatedFloorPlanData = {
        ...floorPlanData,
        customization: updatedCustomization
      };

      // Update localStorage - userProjects
      const cachedProjects = localStorage.getItem("userProjects");
      if (cachedProjects) {
        const projects = JSON.parse(cachedProjects);
        const updatedProjects = projects.map((project: any) =>
          project.id.toString() === projectId
            ? {
                ...project,
                plans: project.plans.map((plan: any) =>
                  plan.id.toString() === planId
                    ? { ...plan, coordinates: updatedFloorPlanData }
                    : plan
                )
              }
            : project
        );
        localStorage.setItem("userProjects", JSON.stringify(updatedProjects));
      }

      // Update localStorage - currentPlan
      const cachedCurrentPlan = localStorage.getItem("currentPlan");
      if (cachedCurrentPlan) {
        const currentPlans = JSON.parse(cachedCurrentPlan);
        const planKey = `${projectId}/${planId}`;
        if (currentPlans[planKey]) {
          currentPlans[planKey].coordinates = updatedFloorPlanData;
          localStorage.setItem("currentPlan", JSON.stringify(currentPlans));
        }
      }

      // Save to backend
      const coordinatesPayload = {
        coordinates: updatedFloorPlanData
      };

      const response = await fetch(
        `${API_BASE_URL}/projects/${projectId}/plans/${planId}/`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(coordinatesPayload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save customization');
      }

      console.log('Customization saved successfully');
      
      // Update context's floorPlanData so Generated.tsx re-renders
      setFloorPlanData(updatedFloorPlanData);
      
      return true;
    } catch (error) {
      console.error('Error saving customization:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Reset all floor customizations
  const handleResetCustomizations = async () => {
    if (!projectId || !planId) {
      console.error('Missing projectId or planId');
      return false;
    }

    try {
      setIsSaving(true);
      const token = await getValidAccessToken();
      if (!token) {
        console.error('No access token found');
        setIsSaving(false);
        return false;
      }

      // Get current customization
      const currentCustomization = (floorPlanData as any)?.customization || { wall: {}, floor: {} };
      
      // Clear floor customizations
      const updatedCustomization = {
        ...currentCustomization,
        floor: {} // Clear all floor customizations
      };

      // Merge with floor plan data
      const updatedFloorPlanData = {
        ...floorPlanData,
        customization: updatedCustomization
      };

      // Update localStorage - userProjects
      const cachedProjects = localStorage.getItem("userProjects");
      if (cachedProjects) {
        const projects = JSON.parse(cachedProjects);
        const updatedProjects = projects.map((project: any) =>
          project.id.toString() === projectId
            ? {
                ...project,
                plans: project.plans.map((plan: any) =>
                  plan.id.toString() === planId
                    ? { ...plan, coordinates: updatedFloorPlanData }
                    : plan
                )
              }
            : project
        );
        localStorage.setItem("userProjects", JSON.stringify(updatedProjects));
      }

      // Update localStorage - currentPlan
      const cachedCurrentPlan = localStorage.getItem("currentPlan");
      if (cachedCurrentPlan) {
        const currentPlans = JSON.parse(cachedCurrentPlan);
        const planKey = `${projectId}/${planId}`;
        if (currentPlans[planKey]) {
          currentPlans[planKey].coordinates = updatedFloorPlanData;
          localStorage.setItem("currentPlan", JSON.stringify(currentPlans));
        }
      }

      // Save to backend
      const coordinatesPayload = {
        coordinates: updatedFloorPlanData
      };

      const response = await fetch(
        `${API_BASE_URL}/projects/${projectId}/plans/${planId}/`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(coordinatesPayload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reset customizations');
      }

      console.log('Customizations reset successfully');
      
      // Update context's floorPlanData so Generated.tsx re-renders
      setFloorPlanData(updatedFloorPlanData);
      
      // Clear local visualization options
      updateVisualizationOption('customRoomTextures', {});
      
      return true;
    } catch (error) {
      console.error('Error resetting customizations:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Filter textures based on search
  const filteredTextures = useMemo(() => {
    if (!textureSearch) return FLOOR_TEXTURES;
    return FLOOR_TEXTURES.filter(texture =>
      texture.name.toLowerCase().includes(textureSearch.toLowerCase())
    );
  }, [textureSearch]);

  // Calculate mini floor plan bounds
  const miniFloorPlanBounds = useMemo(() => {
    if (rooms.length === 0) return { minX: 0, maxX: 100, minZ: 0, maxZ: 100, width: 100, height: 100 };
    
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    
    rooms.forEach((room: Room) => {
      room.floor_polygon.forEach((point: Point) => {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minZ = Math.min(minZ, point.z);
        maxZ = Math.max(maxZ, point.z);
      });
    });
    
    return {
      minX,
      maxX,
      minZ,
      maxZ,
      width: maxX - minX,
      height: maxZ - minZ
    };
  }, [rooms]);

  return (
    <div 
      ref={panelRef}
      className="vizpanel-container"
      onMouseDown={blockAllEvents}
      onMouseMove={blockAllEvents}
      onMouseUp={blockAllEvents}
      onClick={blockAllEvents}
      onDoubleClick={blockAllEvents} 
      onWheel={blockWheelEvents}
      style={{userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",}}
    >
      <div className="vizpanel-header">
        <h2>Visualization Options</h2>
        <button className="vizpanel-close-btn" onClick={(e) => {
          blockAllEvents(e);
          onClose();
        }}>×</button>
      </div>
      
      <div className="vizpanel-content">
        <div className="vizpanel-section">
          <h3>Display Options</h3>
          
          <div className="vizpanel-toggle-option">
            <label>
              <input
                type="checkbox"
                checked={visualizationOptions.showMeasurements}
                onChange={() => handleToggle('showMeasurements')}
              />
              Show Measurements
            </label>
          </div>
          
          <div className="vizpanel-toggle-option">
            <label>
              <input
                type="checkbox"
                checked={visualizationOptions.showRoomLabels}
                onChange={() => handleToggle('showRoomLabels')}
              />
              Show Room Labels
            </label>
          </div>
          
          <div className="vizpanel-toggle-option">
            <label>
              <input
                type="checkbox"
                checked={visualizationOptions.showGrid}
                onChange={() => handleToggle('showGrid')}
              />
              Show Grid
            </label>
          </div>
        </div>
        
        <div className="vizpanel-section">
          <h3>Color Scheme</h3>
          
          <div className="vizpanel-color-preview">
            <div 
              className={`vizpanel-color-sample-container ${visualizationOptions.colorScheme === 'custom' ? 'vizpanel-color-selected' : ''}`}
              onClick={(e) => {
                blockAllEvents(e);
                handleColorSampleClick('custom');
              }}
            >
              <div className="vizpanel-color-sample vizpanel-color-custom"></div>
              <span className="vizpanel-color-label">Custom</span>
            </div>
            <div 
              className={`vizpanel-color-sample-container ${visualizationOptions.colorScheme === 'standard' ? 'vizpanel-color-selected' : ''}`}
              onClick={(e) => {
                blockAllEvents(e);
                handleColorSampleClick('standard');
              }}
            >
              <div className="vizpanel-color-sample vizpanel-color-standard"></div>
              <span className="vizpanel-color-label">Standard</span>
            </div>
            <div 
              className={`vizpanel-color-sample-container ${visualizationOptions.colorScheme === 'monochrome' ? 'vizpanel-color-selected' : ''}`}
              onClick={(e) => {
                blockAllEvents(e);
                handleColorSampleClick('monochrome');
              }}
            >
              <div className="vizpanel-color-sample vizpanel-color-monochrome"></div>
              <span className="vizpanel-color-label">Monochrome</span>
            </div>
            <div 
              className={`vizpanel-color-sample-container ${visualizationOptions.colorScheme === 'pastel' ? 'vizpanel-color-selected' : ''}`}
              onClick={(e) => {
                blockAllEvents(e);
                handleColorSampleClick('pastel');
              }}
            >
              <div className="vizpanel-color-sample vizpanel-color-pastel"></div>
              <span className="vizpanel-color-label">Pastel</span>
            </div>
            <div 
              className={`vizpanel-color-sample-container ${visualizationOptions.colorScheme === 'contrast' ? 'vizpanel-color-selected' : ''}`}
              onClick={(e) => {
                blockAllEvents(e);
                handleColorSampleClick('contrast');
              }}
            >
              <div className="vizpanel-color-sample vizpanel-color-contrast"></div>
              <span className="vizpanel-color-label">Contrast</span>
            </div>
          </div>
        </div>
        
        {/* Custom Texture Section */}
        {visualizationOptions.colorScheme === 'custom' && (
          <div className="vizpanel-section vizpanel-custom-section">
            <h3>Custom Floor Textures</h3>
            
            {/* Room Selection Controls */}
            <div className="vizpanel-room-controls">
              <div className="vizpanel-control-buttons">
                <button
                  className={`vizpanel-mode-btn ${!multiSelectMode ? 'active' : ''}`}
                  onClick={(e) => {
                    blockAllEvents(e);
                    setMultiSelectMode(false);
                  }}
                >
                  Single
                </button>
                <button
                  className={`vizpanel-mode-btn ${multiSelectMode ? 'active' : ''}`}
                  onClick={(e) => {
                    blockAllEvents(e);
                    setMultiSelectMode(true);
                  }}
                >
                  Multiple
                </button>
                <button
                  className="vizpanel-select-all-btn"
                  onClick={(e) => {
                    blockAllEvents(e);
                    handleSelectAllRooms();
                  }}
                >
                  {selectedRooms.size === rooms.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>

            {/* Mini Floor Plan */}
            <div className="vizpanel-mini-floorplan">
              <svg
                viewBox={`${miniFloorPlanBounds.minX - 20} ${miniFloorPlanBounds.minZ - 20} ${miniFloorPlanBounds.width + 40} ${miniFloorPlanBounds.height + 40}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ width: '100%', height: '120px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f9fafb' }}
              >
                {rooms.map((room: Room) => {
                  const points = room.floor_polygon
                    .map((p: Point) => `${p.x},${p.z}`)
                    .join(' ');
                  const isSelected = selectedRooms.has(room.id);
                  const hasTexture = visualizationOptions.customRoomTextures?.[room.id];
                  
                  return (
                    <polygon
                      key={room.id}
                      points={points}
                      fill={isSelected ? '#3b82f6' : (hasTexture ? '#10b981' : '#d1d5db')}
                      stroke="#374151"
                      strokeWidth="2"
                      opacity={isSelected ? 0.8 : 0.6}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRoomSelect(room.id)}
                    />
                  );
                })}
              </svg>
              <div className="vizpanel-selection-hint">
                <span style={{ color: '#3b82f6' }}>●</span> Selected
                <span style={{ color: '#10b981', marginLeft: '12px' }}>●</span> Has Texture
                <span style={{ color: '#d1d5db', marginLeft: '12px' }}>●</span> Default
              </div>
            </div>

            {/* Texture/Color Tabs */}
            <div className="vizpanel-tabs">
              <button
                className={`vizpanel-tab ${activeTab === 'texture' ? 'active' : ''}`}
                onClick={(e) => {
                  blockAllEvents(e);
                  setActiveTab('texture');
                }}
              >
                Texture
              </button>
              <button
                className={`vizpanel-tab ${activeTab === 'color' ? 'active' : ''}`}
                onClick={(e) => {
                  blockAllEvents(e);
                  setActiveTab('color');
                }}
              >
                Color
              </button>
            </div>

            {/* Texture Selection */}
            {activeTab === 'texture' && (
              <div className="vizpanel-texture-section">

                {/* Texture Grid - Fixed with proper scrolling */}
                <div 
                  ref={textureGridRef}
                  className="vizpanel-texture-grid"
                  style={{
                    maxHeight: 'calc(60vh - 0px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {filteredTextures.map((texture) => (
                    <div
                      key={texture.id}
                      className={`vizpanel-texture-item ${selectedTexture === texture.id ? 'selected' : ''}`}
                      onClick={(e) => {
                        blockAllEvents(e);
                        setSelectedTexture(texture.id);
                      }}
                    >
                      <div 
                        className="vizpanel-texture-preview"
                        style={{
                          backgroundImage: `url(/textures/${texture.id}.jpg)`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      <span className="vizpanel-texture-name">{texture.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {activeTab === 'color' && (
              <div className="vizpanel-color-section">
                <div className="vizpanel-color-picker">
                  <label>Select Floor Color:</label>
                  <input
                    type="color"
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                  />
                  <div 
                    className="vizpanel-color-preview-large"
                    style={{ backgroundColor: selectedColor }}
                  />
                </div>
              </div>
            )}

            {/* Apply Button */}
            <div className="vizpanel-action-buttons">
              <button
                className="vizpanel-apply-texture-btn"
                disabled={selectedRooms.size === 0 || (!selectedTexture && activeTab === 'texture') || (!selectedColor && activeTab === 'color') || isSaving}
                onClick={(e) => {
                  blockAllEvents(e);
                  handleApplyTexture();
                }}
              >
                {isSaving ? 'Saving...' : `Apply to ${selectedRooms.size} Room${selectedRooms.size !== 1 ? 's' : ''}`}
              </button>
              
              <button
                className="vizpanel-reset-custom-btn"
                disabled={isSaving || !visualizationOptions.customRoomTextures || Object.keys(visualizationOptions.customRoomTextures).length === 0}
                onClick={(e) => {
                  blockAllEvents(e);
                  handleResetCustomizations();
                }}
                title="Reset all customizations"
              >
                ↻
              </button>
            </div>
          </div>
        )}
        
        <div className="vizpanel-actions">
          <button 
            className="vizpanel-reset-btn" 
            onClick={(e) => {
              blockAllEvents(e);
              resetVisualizationOptions();
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisualizationPanel;